import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation,useParams } from 'react-router-dom';
import ClientHeaderNavigation from '../../components/ui/ClientHeaderNavigation';
import BookingProgressNavigation from '../../components/ui/BookingProgressNavigation';
import BookingSummaryPanel from './components/BookingSummaryPanel';
import ClientDetailsFormFields from './components/ClientDetailsFormFields';
import Icon from '../../components/AppIcon';
import { useService } from '../../components/context/ServiceContext';
import { useBooking } from '../../components/context/BookingContext';
import { useCompany } from '../../components/context/CompanyContext';
import repositoryContainer from '../../services/RepositoryContainer';
import PoweredBy from 'components/PoweredBy';
import { storeChatAccessToken } from '../../utils/chatAccess';
import { isRangePriced, needsPriceFinalization } from '../../utils/pricingUtils';




const ClientDetailsForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyPath } = useParams();
  const { selectedDate, selectedDateSlotId, selectedTimeSlot, selectedTimeSlotId, resetBooking } = useBooking();
  const { company } = useCompany();
  const formatDate = (date) => {
    if (!date) return '';
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date)?.toLocaleDateString('en-US', options);
  };
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    specialRequests: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const { selectedServices} = useService();
  const service = selectedServices?.[0];

  const bookingDetails = location?.state?.bookingDetails || {
    date: selectedDate ? formatDate(selectedDate) : 'Not selected',
    dateSlotId: selectedDateSlotId || null,
    time: selectedTimeSlot? selectedTimeSlot.time : 'Not selected',
    timeSlotId: selectedTimeSlotId || null,
    service: service? service.name : 'Not selected',
    price: service? parseFloat(service.price) : 0,
    pricingMode: service?.pricingMode || 'fixed',
    minimumPrice: service?.minimumPrice ?? null,
    maximumPrice: service?.maximumPrice ?? null,
  };
  const serviceNeedsFinalPricing = isRangePriced(service) || isRangePriced(bookingDetails);

  // Create booking
const createBooking = async (bookingData) => {
  return repositoryContainer.booking.create({
    clientName: bookingData.clientName,
    clientEmail: bookingData.clientEmail,
    clientPhone: bookingData.clientPhone,
    timeSlotId: bookingData.timeSlotId,
    dateSlotId: bookingData.dateSlotId,
    serviceItemIds: bookingData.serviceItemIds,
    note: bookingData.note
  });
};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!company?.requiresPayment || !companyPath || !bookingDetails.price || serviceNeedsFinalPricing) {
      setPaymentQuote(null);
      return;
    }
    let active = true;
    setIsQuoteLoading(true);
    repositoryContainer.order.calculateFees(bookingDetails.price, companyPath)
      .then((raw) => {
        if (active) setPaymentQuote(raw?.response ?? raw);
      })
      .catch(() => {
        if (active) setPaymentQuote(null);
      })
      .finally(() => {
        if (active) setIsQuoteLoading(false);
      });
    return () => { active = false; };
  }, [bookingDetails.price, company?.requiresPayment, companyPath, serviceNeedsFinalPricing]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Please enter your full name';
    } else if (formData?.name?.trim()?.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    const phoneDigits = formData?.phone?.replace(/\D/g, '');
    if (!phoneDigits) {
      newErrors.phone = 'Please enter your phone number';
    } else if (phoneDigits?.length !== 10) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData?.email?.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!emailRegex?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidPattern.test(selectedDateSlotId || '') || !guidPattern.test(selectedTimeSlotId || '')) {
      setErrors({
        submit: 'Your date or time selection is no longer valid. Please return and select it again.'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingPayload = {
        clientName: formData.name.trim(),
        clientEmail: formData.email.trim(),
        clientPhone: formData.phone.trim(),
        timeSlotId: selectedTimeSlotId,
        dateSlotId: selectedDateSlotId,
        serviceItemIds: service ? [service.id] : [],
        note: formData.specialRequests
      };

      const raw = await createBooking(bookingPayload);
      const bookingResponse = raw?.response ?? raw;

      if (!bookingResponse?.referenceCode) {
        throw new Error('Booking created but no reference code received');
      }

      storeChatAccessToken(
        'booking',
        bookingResponse.referenceCode,
        bookingResponse.chatAccessToken
      );

      const requiresFinalPricing = needsPriceFinalization(bookingResponse) || serviceNeedsFinalPricing;

      if (company?.requiresPayment && bookingResponse.id && !requiresFinalPricing) {
        // Persist confirmation data before the Paystack redirect wipes React state
        const bookingConfirmation = {
          ...bookingDetails,
          clientDetails: formData,
          referenceCode: bookingResponse.referenceCode,
          confirmationNumber: bookingResponse.referenceCode,
        };
        localStorage.setItem(
          `clawfe:pendingBooking:${companyPath}`,
          JSON.stringify(bookingConfirmation)
        );

        const callbackUrl = `${window.location.origin}/${companyPath}/booking-callback`;
        const paymentRaw = await repositoryContainer.booking.initiatePayment(
          bookingResponse.id,
          callbackUrl
        );
        const paymentData = paymentRaw?.response ?? paymentRaw;
        localStorage.setItem(
          `clawfe:pendingBooking:${companyPath}`,
          JSON.stringify({
            ...bookingConfirmation,
            referenceCode: paymentData.sourceReferenceCode || bookingResponse.referenceCode,
            confirmationNumber: paymentData.sourceReferenceCode || bookingResponse.referenceCode,
            paymentReference: paymentData.reference,
          })
        );
        window.location.href = paymentData.authorizationUrl;
        return;
      }

      const bookingConfirmation = {
        ...bookingDetails,
        clientDetails: formData,
        referenceCode: bookingResponse.referenceCode,
        confirmationNumber: bookingResponse.referenceCode,
        requiresPriceFinalization: requiresFinalPricing,
      };

      navigate(`/${companyPath}/booking-confirmation`, {
        state: { bookingConfirmation },
        replace: true
      });
    } catch (error) {
      console.error('Booking creation failed:', error);
      const validationErrors = error?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors).flat().find(Boolean)
        : null;
      setErrors({
        submit: firstValidationError
          || error?.data?.message
          || 'Failed to create booking. Please try again.'
      });
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/${companyPath}/time-slot-selection`, {
      state: {
        selectedDate,
        selectedDateSlotId,
        selectedTimeSlot,
        selectedTimeSlotId
      }
    });
  };

  const handleRestartBooking = () => {
    navigate(`/${companyPath}/date-selection`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <ClientHeaderNavigation onRestartBooking={handleRestartBooking} />
      <BookingProgressNavigation
        currentStep={3}
        totalSteps={3}
        canGoBack={true}
        canGoForward={false}
        onBack={handleBack}
        steps={[
          { label: 'Select Date', path: `/${companyPath}/date-selection` },
          { label: 'Choose Time', path: `/${companyPath}/time-slot-selection` },
          { label: 'Your Details', path: `/${companyPath}/client-details-form` }
        ]}
      />
      <main className="pt-5 md:pt-5 lg:pt-5 pb-12 md:pb-16 lg:pb-20 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 md:mb-8 lg:mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-primary to-secondary rounded-full mb-4 md:mb-6 shadow-lg">
              <Icon name="UserCheck" size={32} color="white" />
            </div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2 md:mb-3">
              Almost There!
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground font-caption max-w-2xl mx-auto">
              Just a few details to complete your booking. We'll send you a confirmation email shortly.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <div className="bg-card rounded-xl md:rounded-2xl shadow-md p-4 md:p-6 lg:p-8 border border-border">
                <div className="flex items-center gap-3 mb-4 md:mb-6 lg:mb-8">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                    <Icon name="User" size={20} color="white" />
                  </div>
                  <div>
                    <h2 className="font-heading font-semibold text-lg md:text-xl lg:text-2xl text-foreground">
                      Your Information
                    </h2>
                    <p className="text-xs md:text-sm text-muted-foreground font-caption">
                      Required fields are marked with *
                    </p>
                  </div>
                </div>

                {errors?.submit && (
                  <div className="mb-4 md:mb-6 p-3 md:p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
                    <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                    <p className="text-sm md:text-base text-error font-caption">
                      {errors?.submit}
                    </p>
                  </div>
                )}

                <ClientDetailsFormFields
                  formData={formData}
                  errors={errors}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  paymentQuote={paymentQuote}
                  isPaymentRequired={company?.requiresPayment}
                  isQuoteLoading={isQuoteLoading}
                  requiresPriceFinalization={serviceNeedsFinalPricing}
                />
              </div>
            </div>

            <div className="lg:col-span-1 order-1 lg:order-2">
              <div className="lg:sticky lg:top-40">
                <BookingSummaryPanel
                  bookingDetails={bookingDetails}
                  paymentQuote={paymentQuote}
                  isPaymentRequired={company?.requiresPayment}
                  isQuoteLoading={isQuoteLoading}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8 lg:mt-10 text-center">
            <div className="inline-flex items-center gap-2 text-xs md:text-sm text-muted-foreground font-caption">
              <Icon name="Lock" size={16} className="text-success" />
              <span>Secure booking </span> <PoweredBy logoUrl="/steinb.png" height={50} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientDetailsForm;
